// Lo que muestra la portada de Crear según el rubro de la marca
// (ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro): el abanico de Imagen, el de Video y la
// demo del Asistente. Moda e Indumentaria (y cualquier rubro sin juego propio) ve lo de
// siempre —las plantillas globales, los clips de los tipos de video y el sweater—; Belleza,
// Hogar y Decoración y Gastronomía ven sus productos, los mismos del onboarding de su rubro.
//
// Puro: sólo rutas y textos. Los videos están achicados a 480 px y sin audio
// (`public/images/crear_rubros/<rubro>/`, con su póster al lado), como los de la home.

import { RUBRO_BELLEZA, RUBRO_HOGAR_DECO } from '../../../../lib/rubro';

export type RubroDeCrear = 'moda' | 'belleza' | 'hogar' | 'gastronomia';

/** El label exacto del rubro en el select del onboarding (`OnboardingSelects`). */
export const RUBRO_GASTRONOMIA = 'Gastronomía';

/** Qué juego ve la marca. Sin rubro o con uno sin juego propio, el de Moda (el de siempre). */
export function rubroDeCrear(rubro: string | null | undefined): RubroDeCrear {
  if (rubro === RUBRO_BELLEZA) return 'belleza';
  if (rubro === RUBRO_HOGAR_DECO) return 'hogar';
  if (rubro === RUBRO_GASTRONOMIA) return 'gastronomia';
  return 'moda';
}

const ONB = '/images/onboarding/rubros';
const CLIPS = '/images/crear_rubros';

/**
 * Las fotos del abanico de Imagen. Moda no está: usa las plantillas globales del bucket
 * (`CarruselDePlantillas`), que son de ropa.
 */
export const IMAGENES_DEL_ABANICO: Readonly<Record<Exclude<RubroDeCrear, 'moda'>, readonly string[]>> = {
  belleza: [
    `${ONB}/belleza/carousel/step-1.jpg`,
    `${ONB}/belleza/lapiz/catalogo.jpg`,
    `${ONB}/belleza/carousel/step-2.jpg`,
    `${ONB}/belleza/mascara/redes.jpg`,
    `${ONB}/belleza/carousel/step-4.jpg`,
    `${ONB}/belleza/lapiz/redes.jpg`,
    `${ONB}/belleza/mascara/catalogo.jpg`,
    '/images/ejemplos_modos/anuncio/anuncio-belleza-polvo.jpg',
  ],
  hogar: [
    `${ONB}/hogar/carousel/step-1.jpg`,
    `${ONB}/hogar/silla/redes.jpg`,
    `${ONB}/hogar/carousel/step-2.jpg`,
    `${ONB}/hogar/mesa-luz/redes.jpg`,
    `${ONB}/hogar/carousel/step-4.jpg`,
    `${ONB}/hogar/carousel/step-3.jpg`,
    `${ONB}/hogar/silla/catalogo.jpg`,
    `${ONB}/hogar/mesa-luz/catalogo.jpg`,
  ],
  gastronomia: [
    `${ONB}/gastronomia/carousel/step-4.jpg`,
    `${ONB}/gastronomia/pizza/redes.jpg`,
    `${ONB}/gastronomia/carousel/step-3.jpg`,
    `${ONB}/gastronomia/hamburguesa/redes.jpg`,
    `${ONB}/gastronomia/carousel/step-1.jpg`,
    `${ONB}/gastronomia/carousel/step-2.jpg`,
    `${ONB}/gastronomia/hamburguesa/catalogo.jpg`,
    `${ONB}/gastronomia/pizza/catalogo.jpg`,
  ],
};

/** Un clip del abanico de Video, con su póster (el primer cuadro). */
export interface ClipDelAbanico {
  video: string;
  poster: string;
}
const clip = (rubro: string, nombre: string): ClipDelAbanico => ({
  video: `${CLIPS}/${rubro}/${nombre}.mp4`,
  poster: `${CLIPS}/${rubro}/${nombre}.jpg`,
});

/**
 * Los clips del abanico de Video. Moda no está: usa los de los tipos de video del Creador
 * (`useVideoTipos`).
 */
export const VIDEOS_DEL_ABANICO: Readonly<Record<Exclude<RubroDeCrear, 'moda'>, readonly ClipDelAbanico[]>> = {
  // El shampoo (lo sumó Maxi) entre los de las modelos (ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro-2).
  belleza: [
    clip('belleza', 'labial'),
    clip('belleza', 'delineador'),
    clip('belleza', 'shampoo'),
    clip('belleza', 'rimel'),
    clip('belleza', 'rimel-marmol'),
  ],
  // La mesa (la sumó Maxi) al lado de la silla, que es del mismo estilo
  // (ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro-3).
  hogar: [
    clip('hogar', 'silla'),
    clip('hogar', 'mesa'),
    clip('hogar', 'lampara'),
    clip('hogar', 'mesa-de-luz'),
    clip('hogar', 'mesa-de-luz-ambiente'),
  ],
  // La pizza de jamón crudo (la sumó Maxi) lejos de la otra pizza: entre el sushi y, al dar
  // la vuelta, la hamburguesa (ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro-4).
  gastronomia: [
    clip('gastronomia', 'hamburguesa'),
    clip('gastronomia', 'helado'),
    clip('gastronomia', 'pizza'),
    clip('gastronomia', 'sushi'),
    clip('gastronomia', 'pizza-jamon'),
  ],
};

/**
 * El clip de la card "Animar imagen" de abajo de la caja, del rubro de la marca
 * (ADR-2026-10-12-animar-imagen-muestra-un-clip-del-rubro). Moda —y cualquier rubro sin juego
 * propio— ve el del abrigo de la home.
 */
export const CLIP_DE_ANIMAR: Readonly<Record<RubroDeCrear, ClipDelAbanico>> = {
  moda: { video: '/images/home_vertientes/redes_moda.mp4', poster: `${CLIPS}/moda/abrigo.jpg` },
  belleza: clip('belleza', 'labial'),
  hogar: clip('hogar', 'silla'),
  gastronomia: clip('gastronomia', 'hamburguesa'),
};

/** Una placa del carrusel de la demo. `llamada`: lleva la flecha de "comprá" (hoy ninguna). */
export interface PlacaDeLaDemo {
  foto: string;
  texto: string;
  llamada?: boolean;
}

/**
 * La demo del Asistente: la foto del producto que "manda" el usuario y las cuatro placas que
 * arma Cumbre, todas del MISMO producto y en lugares distintos; las que se parecen van
 * separadas, no una al lado de la otra (ADR-2026-10-12-la-demo-arma-cuatro-imagenes; antes
 * tres, y antes cinco).
 */
export const DEMO_POR_RUBRO: Readonly<Record<RubroDeCrear, { producto: string; placas: readonly PlacaDeLaDemo[] }>> = {
  moda: {
    producto: '/images/ejemplos_modos/catalogo/catalogo-5.jpg',
    placas: [
      { foto: '/images/ejemplos_modos/catalogo/catalogo-1.jpg', texto: 'Nueva colección' },
      // Un cuadro del clip del sweater (en una terraza): las otras son del mismo estudio.
      { foto: `${CLIPS}/moda/placa-sweater-terraza.jpg`, texto: 'Para las tardes frescas' },
      { foto: '/images/ejemplos_modos/catalogo/catalogo-4.jpg', texto: 'El detalle del sol' },
      { foto: '/images/ejemplos_modos/catalogo/catalogo-2.jpg', texto: 'Suave y abrigado' },
    ],
  },
  belleza: {
    producto: `${ONB}/belleza/lapiz/fondo-blanco.jpg`,
    placas: [
      { foto: `${ONB}/belleza/lapiz/redes.jpg`, texto: 'Nuevo labial' },
      { foto: `${ONB}/belleza/lapiz/catalogo.jpg`, texto: 'Rojo intenso' },
      { foto: `${ONB}/belleza/lapiz/video-poster.jpg`, texto: 'Fácil de aplicar' },
      { foto: `${ONB}/belleza/lapiz/original.jpg`, texto: 'Dura todo el día' },
    ],
  },
  hogar: {
    producto: `${ONB}/hogar/silla/fondo-blanco.jpg`,
    placas: [
      { foto: `${ONB}/hogar/silla/redes.jpg`, texto: 'Nueva colección' },
      { foto: `${ONB}/hogar/silla/catalogo.jpg`, texto: 'Patas de madera' },
      { foto: `${ONB}/hogar/silla/video-poster.jpg`, texto: 'Ideal para tu living' },
      { foto: `${ONB}/hogar/silla/product.jpg`, texto: 'Cómoda y liviana' },
    ],
  },
  gastronomia: {
    producto: `${ONB}/gastronomia/hamburguesa/fondo-blanco.jpg`,
    placas: [
      { foto: `${ONB}/gastronomia/hamburguesa/redes.jpg`, texto: 'Nueva en la carta' },
      { foto: `${ONB}/gastronomia/hamburguesa/catalogo.jpg`, texto: 'Doble cheddar' },
      { foto: `${ONB}/gastronomia/hamburguesa/video-poster.jpg`, texto: 'Para disfrutar' },
      { foto: `${ONB}/gastronomia/carousel/step-4.jpg`, texto: 'Recién hecha' },
    ],
  },
};
